import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ICampaignSetupSlice,
  IExperienceRange,
  IFolderDetails,
  IJobLocations,
  ISalaryRange,
  TJobMode,
} from "../../types/campaign.types";

const initialState: ICampaignSetupSlice = {
  jobTitle: "",
  jobLocations: [],
  jobMode: "WFO",
  experienceRange: {
    min: "",
    max: "",
  },
  salaryRange: {
    min: "",
    max: "",
    currency: "",
  },
  campaignName: "",
  selectedFolder: null,
  numberOfSelectedCandidates: "",
  isSendToAlreadyContacted: true,
  noticePeriod: "",
  isSalaryRangeFilledInSetup: false,
};

const campaignSetupSlice = createSlice({
  name: "campaignSetup",
  initialState,
  reducers: {
    setJobTitle(state, action: PayloadAction<{ jobTitle: string }>) {
      state.jobTitle = action.payload.jobTitle;
    },
    setJobLocations(
      state,
      action: PayloadAction<{ jobLocations: IJobLocations[] }>
    ) {
      state.jobLocations = action.payload.jobLocations;
    },
    setJobMode(state, action: PayloadAction<{ jobMode: TJobMode }>) {
      state.jobMode = action.payload.jobMode;
    },
    setNoticePeriod(state, action: PayloadAction<{ noticePeriod: string }>) {
      state.noticePeriod = action.payload.noticePeriod;
    },
    setExperienceRange(
      state,
      action: PayloadAction<{ experienceRange: IExperienceRange }>
    ) {
      state.experienceRange = action.payload.experienceRange;
    },
    setSalaryRange(
      state,
      action: PayloadAction<{ salaryRange: ISalaryRange }>
    ) {
      state.salaryRange = action.payload.salaryRange;
    },
    resetSalaryRange(state) {
      state.salaryRange = {
        min: "",
        max: "",
        currency: "INR",
      };
    },
    setIsSalaryRangeFilledInSetup(state, action:PayloadAction<{value: boolean}>){
      state.isSalaryRangeFilledInSetup = action.payload.value;
    },
    setCampaignName(state, action: PayloadAction<{ campaignName: string }>) {
      state.campaignName = action.payload.campaignName;
    },
    setNumberOfSelectedCandidates(
      state,
      action: PayloadAction<{ numberOfSelectedCandidates: string }>
    ) {
      state.numberOfSelectedCandidates =
        action.payload.numberOfSelectedCandidates;
    },
    setSelectedFolder(
      state,
      action: PayloadAction<{ selectedFolder: IFolderDetails | null }>
    ) {
      state.selectedFolder = action.payload.selectedFolder;
    },
    setIsSendToAlreadyContacted(
      state,
      action: PayloadAction<{ isSendToAlreadyContacted: boolean }>
    ) {
      state.isSendToAlreadyContacted = action.payload.isSendToAlreadyContacted;
    },
    setCampaignSetupDetails(
      state,
      action: PayloadAction<{
        campaignDetails: {
          jobLocations?: IJobLocations[] | null;
          jobTitle: string | null;
          maxExp: string | null;
          maxSalary: string | null;
          minExp: string | null;
          minSalary: string | null;
          campaignName?: string | null;
          currency?: string | null;
          selectedCandidates?: number;
          selectedFolderId?: number;
          isSendToAlreadyContacted?: boolean;
          jobMode?: TJobMode;
          noticePeriod?: string;
        };
      }>
    ) {
      const campaignDetails = action.payload.campaignDetails;

      if (campaignDetails.jobLocations) {
        state.jobLocations = campaignDetails.jobLocations;
      }

      if (campaignDetails.jobTitle) {
        state.jobTitle = campaignDetails.jobTitle;
      }

      if (campaignDetails.jobMode) {
        state.jobMode = campaignDetails.jobMode;
      }

      if (campaignDetails.campaignName) {
        state.campaignName = campaignDetails.campaignName;
      }

      const experienceRange = {
        min: "",
        max: ""
      }

      if(campaignDetails?.minExp){
        experienceRange.min = campaignDetails.minExp
      }
      if(campaignDetails?.maxExp){
        experienceRange.max = campaignDetails.maxExp
      }
      state.experienceRange = experienceRange

      if (campaignDetails.maxSalary || campaignDetails.minSalary) {
        state.salaryRange = {
          currency: campaignDetails.currency || "",
          min: campaignDetails.minSalary ? `${campaignDetails.minSalary}` : "",
          max: campaignDetails.maxSalary ? `${campaignDetails.maxSalary}` : "",
        };
      }

      if (typeof campaignDetails?.isSendToAlreadyContacted !== "undefined") {
        state.isSendToAlreadyContacted =
          campaignDetails.isSendToAlreadyContacted;
      }

      if (campaignDetails?.selectedCandidates) {
        state.numberOfSelectedCandidates = `${campaignDetails.selectedCandidates}`;
      }

      if(campaignDetails?.noticePeriod){
        state.noticePeriod = campaignDetails.noticePeriod;
      }
    },
  },
});

const campaignSetupReducer = campaignSetupSlice.reducer;
const campaignSetupActions = campaignSetupSlice.actions;

export { campaignSetupActions };
export default campaignSetupReducer;
